import i18n from '@/utils/i18n';
import React from 'react';
import validator from 'validator';
import {
  Account_InviteRestaurantEmployeeMutation,
  Account_InviteRestaurantEmployeeMutationVariables,
  ContactInfoInput,
  EmployeeRole,
} from '@/types';
import { EmployeeRestaurant, useUserContext } from '@/contexts/user-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { getCodeFromGQLError } from '@/utils/apollo-client';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { InviteEmployeeErrorCode } from '@platform/errors';
import { RESTAURANT_EMPLOYEES_QUERY } from './use-restaurant-employees';
import { SelectedMatchingAccountType } from '@/screens/restaurant/matching-account-action-sheet';
import { Text } from 'react-native';
import { Type } from '@/components/toast';
import { useToasts } from '@/contexts/toast-context';

export const INVITE_EMPLOYEE_MUTATION = gql`
  mutation Account_InviteRestaurantEmployee(
    $name: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid
    $contactInfo: ContactInfoInput!
    $role: String!
  ) {
    inviteRestaurantEmployee(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      name: $name
      employee_role: $role
      contact_info: $contactInfo
    ) {
      employee_id
      is_duplicate_employee
      pepper_url
      whitelabel_android_url
      whitelabel_ios_url
      whitelabel_universal_url
    }
  }
`;

interface BuildContactInfoParams {
  contactEmail: string | null;
  disableDefaultNotifications?: boolean;
  loginEmail: string | null;
  phoneNumber: string | null;
  selectedMatchingAccount?: SelectedMatchingAccountType;
}

const buildContactInfo = ({
  contactEmail,
  disableDefaultNotifications,
  loginEmail,
  phoneNumber,
  selectedMatchingAccount,
}: BuildContactInfoParams): ContactInfoInput => {
  if (selectedMatchingAccount) {
    selectedMatchingAccount === SelectedMatchingAccountType.PHONE
      ? (loginEmail = null)
      : (phoneNumber = null);
  }

  const contactInfo: ContactInfoInput = {
    disable_default_notifications: Boolean(disableDefaultNotifications),
  };

  if (contactEmail) {
    contactInfo.email = contactEmail;
  }
  if (loginEmail) {
    contactInfo.login_email = loginEmail;
  }
  if (phoneNumber) {
    contactInfo.phone_number = phoneNumber;
  }
  return contactInfo;
};

interface UseEmployeeInviteParams {
  contactEmail: string;
  disableDefaultNotifications?: boolean;
  handleNotification?: (notificationData: {
    invitationResponse?: Account_InviteRestaurantEmployeeMutation | null;
    selectedMatchingAccount?: SelectedMatchingAccountType;
    targetRestaurantRelationship: EmployeeRestaurant;
  }) => Promise<void>;
  hasInviteConflictError?: () => void;
  isAdmin: boolean;
  loginEmail: string;
  name: string;
  onClose?: () => void;
  onComplete?: (employeeUUID: string) => void;
  restaurantName: string;
  restaurantRelationship?: EmployeeRestaurant;
  restaurantUUID: string;
  setHasLoginDetailError?: (value: boolean) => void;
  supplierUUID?: string;
  validatedPhoneNumber: string | null;
}

export const useEmployeeInvite = ({
  contactEmail,
  disableDefaultNotifications,
  handleNotification,
  hasInviteConflictError = () => {},
  isAdmin,
  loginEmail,
  name,
  onClose = () => {},
  onComplete,
  restaurantRelationship,
  restaurantUUID,
  setHasLoginDetailError = () => {},
  supplierUUID,
  validatedPhoneNumber,
}: UseEmployeeInviteParams) => {
  const { showErrorToast, showToast } = useToasts();
  const { selectedRestaurantRelationship } = useUserContext();
  const targetRestaurantRelationship =
    restaurantRelationship ?? selectedRestaurantRelationship;

  const [inviteEmployee, { loading }] = useMutation<
    Account_InviteRestaurantEmployeeMutation,
    Account_InviteRestaurantEmployeeMutationVariables
  >(INVITE_EMPLOYEE_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        InviteEmployeeErrorCode.INVITE_EMPLOYEE_PHONE_EMAIL_CONFLICT,
      ],
      noBatch: true,
    },
    refetchQueries: [getOperationName(RESTAURANT_EMPLOYEES_QUERY) as string],
  });

  const handleInviteEmployee = async (
    selectedMatchingAccount?: SelectedMatchingAccountType,
  ) => {
    try {
      // Validate emails if they exist
      const trimmedContactEmail = contactEmail.trim();
      const trimmedLoginEmail = loginEmail.trim();
      const trimmedName = name.trim();

      if (
        (trimmedContactEmail && !validator.isEmail(trimmedContactEmail)) ||
        (trimmedLoginEmail && !validator.isEmail(trimmedLoginEmail))
      ) {
        showErrorToast(i18n.t('toast.invalid_email'));
        return;
      }

      if (!validatedPhoneNumber && !trimmedLoginEmail) {
        setHasLoginDetailError(true);
        return;
      }

      setHasLoginDetailError(false);

      const { data: invitationResponse } = await inviteEmployee({
        variables: {
          contactInfo: buildContactInfo({
            contactEmail: trimmedContactEmail,
            disableDefaultNotifications,
            loginEmail: trimmedLoginEmail,
            phoneNumber: validatedPhoneNumber,
            selectedMatchingAccount,
          }),
          name: trimmedName,
          restaurantUUID,
          role: isAdmin ? EmployeeRole.Admin : EmployeeRole.User,
          supplierUUID:
            supplierUUID || targetRestaurantRelationship.supplierUUID,
        },
      });

      if (handleNotification) {
        await handleNotification({
          invitationResponse,
          selectedMatchingAccount,
          targetRestaurantRelationship,
        });
      }

      if (onComplete) {
        onComplete(
          invitationResponse?.inviteRestaurantEmployee.employee_id ?? '',
        );
      } else {
        onClose();
      }

      if (invitationResponse?.inviteRestaurantEmployee.is_duplicate_employee) {
        showToast({
          content: (
            <>
              <Text style={[FONTS.primaryBold, TYPOGRAPHY.toast]}>
                {i18n.t('invite_restaurant_employee_already_exists.title')}
              </Text>
              <Text style={TYPOGRAPHY.toast}>
                {i18n.t('invite_restaurant_employee_already_exists.message')}
              </Text>
            </>
          ),
          type: Type.POSITIVE,
        });
      }
    } catch (e) {
      if (
        getCodeFromGQLError(e) ===
        InviteEmployeeErrorCode.INVITE_EMPLOYEE_PHONE_EMAIL_CONFLICT
      ) {
        hasInviteConflictError();
      } else {
        showErrorToast();
      }
    }
  };

  return {
    handleInviteEmployee,
    loading,
  };
};
